import { Injectable } from '@angular/core';
import { ImageAnnotatorService } from '../../services/image_anotate.service';
import { RecognitionSystemLibraryService } from '../../recongition-system-api/recongnition-library';
import { ImageVisualizerBox } from './image-visualizer/image-visualizer.types';

@Injectable({ providedIn: 'root' })
export class PageVisualizerService {
  constructor(
    private imageAnnotatorService: ImageAnnotatorService,
    private recognition: RecognitionSystemLibraryService,
  ) {}

  mapInferencesToBoxes(inferences: any[]): ImageVisualizerBox[] {
    return (inferences ?? [])
      .map((inference: any, index: number) => this.mapInferenceToBox(inference, index))
      .filter((box) => box.width > 0 && box.height > 0);
  }

  calculateDurationInSeconds(startedAt?: Date | string, finishedAt?: Date | string): number {
    if (!startedAt || !finishedAt) return 0;
    const start = new Date(startedAt).getTime();
    const finish = new Date(finishedAt).getTime();
    return (finish - start) / 1000;
  }

  async downloadCsv(recognitionId: string): Promise<void> {
    if (!recognitionId) return;
    const inferences = await this.recognition.getInferenceslive(recognitionId);
    const csvContent = this.buildCsvContent(inferences);
    this.generateCsv(csvContent, recognitionId);
  }

  private mapInferenceToBox(inference: any, index: number): ImageVisualizerBox {
    const name = (inference?.name ?? '').trim() || 'No se encontró';
    const isOther = name.split('_').pop()?.toLowerCase() === 'others';
    const color = this.imageAnnotatorService.generateColorFromName(name, Boolean(isOther));
    const box = inference?.bounding_box ?? {};

    const x = this.toNumber(box.x_min ?? box.xmin ?? box.x ?? 0);
    const y = this.toNumber(box.y_min ?? box.ymin ?? box.y ?? 0);
    const xMax = this.toNumber(box.x_max ?? box.xmax ?? box.xMax ?? 0);
    const yMax = this.toNumber(box.y_max ?? box.ymax ?? box.yMax ?? 0);
    let width = this.toNumber(box.width ?? box.w ?? 0);
    let height = this.toNumber(box.height ?? box.h ?? 0);

    if (width <= 0 && xMax > 0) {
      width = xMax - x;
    }

    if (height <= 0 && yMax > 0) {
      height = yMax - y;
    }

    const score = typeof inference?.score === 'number'
      ? ` ${(inference.score * 100).toFixed(1)}%`
      : '';

    return {
      id: inference?.id ?? index,
      x,
      y,
      width,
      height,
      label: name,
      tooltip: `${name}${score}`,
      color: `rgb(${color.r}, ${color.g}, ${color.b})`,
      borderStyle: isOther ? 'dashed' : 'solid',
      metadata: inference,
    };
  }

  private toNumber(value: unknown): number {
    const parsed = Number(value);
    return Number.isFinite(parsed) ? parsed : 0;
  }

  private buildCsvContent(inferences: any[]): string {
    const headers = Object.keys(inferences?.[0] ?? {});
    const csvRows = [headers.join(',')];
    inferences.forEach((det: any) => {
      const values = headers.map((header) => {
        const value = det[header];
        if (typeof value === 'object' && value !== null) {
          return `"${JSON.stringify(value).replace(/"/g, '""')}"`;
        }
        return `"${String(value).replace(/"/g, '""')}"`;
      });
      csvRows.push(values.join(','));
    });
    return csvRows.join('\n');
  }

  private generateCsv(csvContent: string, recognitionId: string): void {
    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.setAttribute('download', `detections_${recognitionId}.csv`);
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);
  }
}
